// src/Payroll_Management/PayRoll_Report/EmployeeSalarySlip.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, RefreshCcw, Printer, FileText } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const MONTHS = [
  { value: "Select", text: "Select" },
  { value: "1", text: "January" },
  { value: "2", text: "February" },
  { value: "3", text: "March" },
  { value: "4", text: "April" },
  { value: "5", text: "May" },
  { value: "6", text: "June" },
  { value: "7", text: "July" },
  { value: "8", text: "August" },
  { value: "9", text: "September" },
  { value: "10", text: "October" },
  { value: "11", text: "November" },
  { value: "12", text: "December" },
];

const MAX_PAGE_BTNS = 5;

const getPageWindow = (current, total, maxBtns) => {
  if (total <= maxBtns) return { start: 1, end: total };
  const half = Math.floor(maxBtns / 2);
  let start = current - half;
  let end = current + half;
  if (maxBtns % 2 === 0) end -= 1;

  if (start < 1) { start = 1; end = maxBtns; }
  if (end > total) { end = total; start = total - maxBtns + 1; }
  return { start, end };
};

const EmployeeSalarySlip = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const [branches, setBranches] = useState([]);
  const [years, setYears] = useState([]);

  const [year, setYear] = useState("Select");
  const [monthValue, setMonthValue] = useState("Select");
  const [branchId, setBranchId] = useState(loginBranchId ? Number(loginBranchId) : 0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const canViewReport = total > 0; // same idea as btn_loademp.Enabled

  const screenBadge = useMemo(() => "INFRA SCREEN : C-48", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const monthName = useMemo(() => {
    const m = MONTHS.find((x) => x.value === String(monthValue));
    return m?.text || "Select";
  }, [monthValue]);

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) { toast.warn("Please select Financial Year."); return; }
    if (!loginBranchId || !branchType) { toast.warn("branchId / branchType missing in localStorage."); return; }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-salary-slip/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setYears(res.data.years || []);
        setBranchId(Number(res.data.defaultBranchId || loginBranchId));
        setMonthValue(String(res.data.defaultMonth || "Select"));
        setYear(String(res.data.defaultYear || "Select"));

        setPage(1);
        await fetchList(1, {
          branchId: Number(res.data.defaultBranchId || loginBranchId),
          monthValue: String(res.data.defaultMonth || "Select"),
          year: String(res.data.defaultYear || "Select"),
        });
      } else {
        toast.error(res?.data?.message || "Failed to load meta");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page, override = null) => {
    if (!String(finYearId).trim()) { toast.warn("Please select Financial Year."); return; }

    const effective = { branchId, year, monthValue, ...(override || {}) };

    if (!effective.year || effective.year === "Select") { toast.warn("Please select Year.."); return; }
    if (!effective.monthValue || effective.monthValue === "Select") { toast.warn("Please select Month.."); return; }
    if (!effective.branchId || String(effective.branchId) === "Select") { toast.warn("Please select Branch.."); return; }

    const monthText = MONTHS.find((m) => m.value === String(effective.monthValue))?.text || "Select";

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-salary-slip/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId: String(effective.branchId),
          year: String(effective.year),
          monthName: monthText,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load salary slip list.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMeta(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchList(page); /* eslint-disable-next-line */ }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = () => fetchList(page);

  const handlePrintAll = () => {
    if (!canViewReport) { toast.warn("No Records Found"); return; }

    // ASP opens EmployeeSalarySlipPrint.aspx using Session selbranchid/selmonth/selyear
    // React opens a print route (we'll build page after you share print ASP or desired layout)
    const url = `/PayRoll/EmployeeSalarySlipPrint?branchId=${branchType === "HO" ? branchId : loginBranchId
      }&month=${encodeURIComponent(monthName)}&year=${encodeURIComponent(year)}`;
    window.open(url, "_blank", "width=900,height=900,scrollbars=yes");
  };

  const handlePrintIndv = (empId) => {
    const url = `/PayRoll/EmployeeSalarySlipPrintIndv?empId=${empId}&year=${encodeURIComponent(
      year
    )}&month=${encodeURIComponent(monthName)}`;
    window.open(url, "_blank", "width=900,height=900,scrollbars=yes");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const { start: pageStart, end: pageEnd } = getPageWindow(page, totalPages, MAX_PAGE_BTNS);
  const visiblePages = Array.from({ length: pageEnd - pageStart + 1 }, (_, i) => pageStart + i);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SALARY SLIP</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Header row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Salary Slip
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Year / Month / Branch → Employee list → Print
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrintAll} className={primaryBtn} disabled={loading || !canViewReport}>
                <FileText className="w-4 h-4" />
                View Report
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              <div>
                <label className={labelBase}>Year <span className="text-red-500">*</span></label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={`${inputBase} mt-2`}>
                  {(years || []).map((y) => (
                    <option key={y.value} value={y.value}>{y.text}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>Month <span className="text-red-500">*</span></label>
                <select value={monthValue} onChange={(e) => setMonthValue(e.target.value)} className={`${inputBase} mt-2`}>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>{m.text}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>Branch <span className="text-red-500">*</span></label>
                <select value={branchId || 0} onChange={(e) => setBranchId(Number(e.target.value))} className={`${inputBase} mt-2`}>
                  {(branches || []).map((b, idx) => (
                    <option key={idx} value={Number(b.fld_branchid)}>{b.fld_branchname}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Branch</th>
                    <th className="px-4 py-4 text-center font-bold">Department</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Print</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 7 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={`${r.fld_employeeid}-${idx}`} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeeid}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_branchname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_departmentname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_designationname}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handlePrintIndv(r.fld_employeeid)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-2xl hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all shadow"
                          >
                            <Printer className="w-4 h-4" />
                            Print
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination (windowed 1-5) */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  First
                </button>

                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {pageStart > 1 && (
                  <button
                    onClick={() => setPage(pageStart - 1)}
                    className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    title="Previous pages"
                  >
                    …
                  </button>
                )}

                {visiblePages.map((pno) => (
                  <button
                    key={pno}
                    onClick={() => setPage(pno)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === pno
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {pno}
                  </button>
                ))}

                {pageEnd < totalPages && (
                  <button
                    onClick={() => setPage(pageEnd + 1)}
                    className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    title="Next pages"
                  >
                    …
                  </button>
                )}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>

                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Last
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeSalarySlip;